import type { OrderStatus } from "@common/logic/order-status";

// The "start your machine" guide, shared by the kiosk and my.bubbles: which
// steps each machine has in each phase, what the header says, and when the
// start counts as failed. No texts, sizes or animations: those are each
// platform's own (the platform maps every step id to its texts).
//
// Payment only sends the start impulse via the moxa. A washer then starts once
// its door is closed and a program is selected on its own display (it shows
// INSERT CREDIT); a dryer as soon as its door is closed, and its temperature
// can't be changed after that.

/**
 * "guide": every step after payment. "prepare": the steps BEFORE payment,
 * ending with `onPrepared`. "after-prepare": after that payment, only waiting
 * for the machine to start.
 */
export type GuidePhase = "guide" | "prepare" | "after-prepare";

export type GuideStatus = "guide" | "running" | "failed" | "prepare" | "starting";

export type GuideMachineKind = "washer" | "dryer";

export type WasherView = "door" | "closed" | "screen" | "running";
export type DryerView = "panel" | "door" | "closed" | "running";

export type GuideStepSpec<View extends string = string> = {
  id: string;
  view: View;
  // the user moves on with a button; without one, the step waits for the moxa
  // (or for `advanceAfterSeconds`)
  confirm: boolean;
  // moves on to the next step by itself after this long (unless the machine
  // runs by then); there's no going back to it
  advanceAfterSeconds?: number;
  // the scene shows a warning next to its drawing on this step
  hasSceneNote?: boolean;
};

const WASHER_LOAD_AND_CLOSE = {
  // phrased as a check, so it works whether or not they've loaded already
  id: "load-and-close",
  view: "door",
  confirm: true,
} as const satisfies GuideStepSpec<WasherView>;

export const WASHER_STEPS = {
  guide: [
    WASHER_LOAD_AND_CLOSE,
    { id: "program", view: "screen", confirm: false },
  ],
  prepare: [
    WASHER_LOAD_AND_CLOSE,
    { id: "program", view: "screen", confirm: true },
  ],
  // paid: the machine starts if it still waits on INSERT CREDIT. Most do within
  // a few seconds; only then ask the customer to check the program.
  "after-prepare": [
    { id: "starting", view: "closed", confirm: false, advanceAfterSeconds: 10 },
    { id: "check-program", view: "screen", confirm: false, hasSceneNote: true },
  ],
} as const satisfies Record<GuidePhase, readonly GuideStepSpec<WasherView>[]>;

const DRYER_TEMPERATURE = {
  // the dryer's temperatures: P1 65 °C … P6 90 °C, + and − move 5 °C
  id: "temperature",
  view: "panel",
  confirm: true,
} as const satisfies GuideStepSpec<DryerView>;

export const DRYER_STEPS = {
  // the impulse goes out once the temperature is confirmed (`sendsStartSignal`)
  guide: [
    DRYER_TEMPERATURE,
    { id: "load-and-close", view: "door", confirm: false },
  ],
  // the door first: the temperature is the last thing before paying
  prepare: [
    { id: "load-and-close", view: "door", confirm: true },
    DRYER_TEMPERATURE,
  ],
  "after-prepare": [{ id: "starting", view: "closed", confirm: false }],
} as const satisfies Record<GuidePhase, readonly GuideStepSpec<DryerView>[]>;

type StepsTable = Record<GuidePhase, readonly GuideStepSpec[]>;

/** Every step id of every phase, for a platform's text table. */
export type GuideTexts<Steps extends StepsTable, Texts> = {
  [Phase in GuidePhase]: Record<Steps[Phase][number]["id"], Texts>;
};

/** A phase's steps with the platform's texts merged in. */
export function stepsWithTexts<
  Spec extends GuideStepSpec,
  Table extends Record<Spec["id"], object>,
>(specs: readonly Spec[], texts: Table): (Spec & Table[keyof Table])[] {
  return specs.map((spec) => ({
    ...spec,
    ...texts[spec.id as keyof Table],
  }));
}

/** The dryer gets the start impulse once this step is confirmed ("guide" phase). */
export const sendsStartSignal = (
  kind: GuideMachineKind,
  phase: GuidePhase,
  step: Pick<GuideStepSpec, "id">,
) => kind === "dryer" && phase === "guide" && step.id === "temperature";

/** Dryer scene: a door step before this one means the door stays shut. */
export const dryerDoorClosed = (
  steps: readonly Pick<GuideStepSpec, "id" | "view">[],
  stepIndex: number,
) => steps.slice(0, stepIndex).some((step) => step.view === "door");

/** Before payment nothing is paid for yet, so nothing can time out. */
export const showsStartTimeout = (phase: GuidePhase) => phase !== "prepare";

/** The program check follows the start by itself: not a step to count. */
export const showsStepDots = (
  kind: GuideMachineKind,
  phase: GuidePhase,
  stepCount: number,
) => (kind === "washer" ? phase !== "after-prepare" : stepCount > 1);

// ---- the start flow (my.bubbles) ----

/**
 * my.bubbles runs the machine set up, the payment and the start as one flow:
 * a stage of it, before the next. The kiosk takes cash between its own steps
 * and has no such flow.
 */
export type StartFlowStage = "prepare" | "payment" | "after-prepare";

/** The steps before payment. */
export const prepareSteps = (kind: GuideMachineKind) =>
  kind === "dryer" ? DRYER_STEPS.prepare : WASHER_STEPS.prepare;

/**
 * The flow's step dots: one per set up step, one for the payment, one for the
 * start (however many steps the start shows: they move on by themselves).
 * `stepIndex` is the step within its stage.
 */
export function startFlowDots(
  kind: GuideMachineKind,
  stage: StartFlowStage,
  stepIndex = 0,
) {
  const prepareCount = prepareSteps(kind).length;
  const active =
    stage === "prepare"
      ? stepIndex
      : stage === "payment"
        ? prepareCount
        : prepareCount + 1;
  return { count: prepareCount + 2, active };
}

/**
 * The dot active on the stage a stage is entered from: every stage is its own
 * page, so its dots start there and move on, instead of jumping. ("prepare"
 * is only entered from a dot when coming back from the payment.)
 */
export function startFlowEnteredFrom(
  kind: GuideMachineKind,
  stage: StartFlowStage,
) {
  const prepareCount = prepareSteps(kind).length;
  return stage === "prepare"
    ? prepareCount
    : stage === "payment"
      ? prepareCount - 1
      : prepareCount;
}

/** What the header shows, in this order of precedence. */
export function guideStatus(
  phase: GuidePhase,
  isRunning: boolean,
  failed: boolean,
): GuideStatus {
  return failed
    ? "failed"
    : isRunning
      ? "running"
      : phase === "prepare"
        ? "prepare"
        : phase === "after-prepare"
          ? "starting"
          : "guide";
}

/**
 * Which part of the guide shows: the failure screen, the success view (only
 * once the drum's spin-up has played in the scene), or the steps.
 */
export function guideView({
  failed,
  isRunning,
  spinUpDone,
}: {
  failed: boolean;
  isRunning: boolean;
  spinUpDone: boolean;
}): "failed" | "running" | "steps" {
  return failed ? "failed" : isRunning && spinUpDone ? "running" : "steps";
}

/**
 * The guide's own countdown ran out: a failure, unless the machine runs by
 * then. `failed` is what the order status already says.
 */
export const guideFailed = ({
  failed,
  timedOut,
  isRunning,
}: {
  failed: boolean;
  timedOut: boolean;
  isRunning: boolean;
}) => failed || (timedOut && !isRunning);

/** The step index after the user's confirm; null on the last step. */
export const nextStepIndex = (stepIndex: number, stepCount: number) =>
  stepIndex < stepCount - 1 ? stepIndex + 1 : null;

/** Back goes to the previous step, unless that one moved on by itself. */
export const canGoBackFrom = (
  steps: readonly Pick<GuideStepSpec, "id" | "advanceAfterSeconds">[],
  stepIndex: number,
) => stepIndex > 0 && !steps[stepIndex - 1]!.advanceAfterSeconds;

// ---- order status ----

/** The machine has this long after the start signal (mirrors the backend). */
export const START_WINDOW_SECONDS = 180;

/** How often the guide asks whether the drum spins. */
export const ORDER_STATUS_POLL_MS = 2000;

export type GuideOrderStatus = Pick<
  OrderStatus,
  "status" | "spinningState" | "startSignalRemainingSeconds"
>;

export const isSpinning = (status: Pick<GuideOrderStatus, "spinningState">) =>
  status.spinningState === "spinning";

export function isStartFailed(status: GuideOrderStatus) {
  return (
    !isSpinning(status) &&
    (status.status === "failed" || status.startSignalRemainingSeconds === 0)
  );
}

/** Polls until the drum spins or the start window runs out. */
export const orderStatusRefetchInterval = (
  status: GuideOrderStatus | undefined,
) => (status && (isSpinning(status) || isStartFailed(status)) ? false : ORDER_STATUS_POLL_MS);

/**
 * The guide's countdown length, fixed at the first answer so the bar doesn't
 * jump on every poll.
 */
export const initialStartTimeoutSeconds = (
  status: Pick<GuideOrderStatus, "startSignalRemainingSeconds"> | undefined,
) => status?.startSignalRemainingSeconds ?? START_WINDOW_SECONDS;

/** The guide covers washers and dryers; anything else keeps its old flow. */
export const guideKindOf = (
  machineType: string | null | undefined,
): GuideMachineKind | null =>
  machineType === "mosogep"
    ? "washer"
    : machineType === "szaritogep"
      ? "dryer"
      : null;
